/**
 * account-rail.tsx — Hàng chọn tài khoản kênh ở cột bộ lọc (máy tính), kiểu Mobile: lưới 6 ô chia đều — "Tất
 * cả" (luôn ở ô đầu) + 4 tài khoản + nút mở rộng. Thay cho ô "Tất cả tài khoản ⌄" cũ.
 *
 * Trật tự lọc đổi theo yêu cầu: chọn KÊNH (ChannelChips, "Tất cả/Zalo cá nhân/…") trước — NẰM NGOÀI, tài
 * khoản hiện ra sau khi đã biết kênh — NẰM TRONG, chỉ còn tài khoản thuộc kênh đó. Dùng lại đúng logic +
 * bản lưu thứ tự của hàng kênh điện thoại (`lib/mobile-channels.ts`, `hooks/use-mobile-channels.ts`) — tên
 * "mobile" chỉ là lịch sử, bản lưu đồng bộ CẢ điện thoại lẫn máy tính của cùng một người.
 *
 * Số ô CỐ ĐỊNH giống hệt điện thoại (`ROW_CELLS`/`ROW_CHANNELS` — không tự đặt `ROW_MAX` riêng như bản đầu):
 * cột bộ lọc máy tính (~208px, hẹp hơn hàng điện thoại full-width) dùng flex `w-9` cố định cho mỗi ô từng bị
 * TRÀN khi đủ 5 tài khoản + nút mở rộng (6×36px + 5 khoảng cách > 208px) — flex tự co khiến ảnh đại diện bị
 * bóp méo, nhãn chữ chồng lên nhau ("quá chặt"). Đổi sang CSS grid 6 cột chia đều (`grid-cols-6`), ảnh đại
 * diện thu nhỏ vừa cột — không bao giờ tràn vì lưới luôn chia đúng 208px/6, bất kể bao nhiêu tài khoản.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronDown, ChevronRight, GripVertical, RotateCcw, Search, Users } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/misc'
import { PlatformBadge, PlatformTag } from '@/components/shared/platform-mark'
import { type ChannelFilterValue } from '@/components/shared/channel-chips'
import { ReconnectButton } from '@/components/shared/reconnect-account'
import { statusMeta, type ChannelAccount } from '@/hooks/use-integrations'
import { otherAccountUnread, shortUnreadCount } from '@/lib/inbox-account'
import { isAccountDown } from '@/lib/account-status'
import { groupOfPlatform, type ChannelGroup } from '@/lib/channel-groups'
import { orderAccounts, visibleAccounts, hasExpand, moveAccountId, ROW_CHANNELS } from '@/lib/mobile-channels'
import { useMobileChannels } from '@/hooks/use-mobile-channels'
import { useDragReorder } from './mobile-segments-sheet'
import { cn, initials } from '@/lib/utils'

/** Icon nhỏ của một kênh (nhóm) — mượn PlatformBadge (vốn vẽ chấm góc avatar) làm icon đứng riêng,
 * đại diện bằng nền tảng ĐẦU TIÊN trong nhóm (vd nhóm "facebook" gồm nhiều nền tảng Meta, lấy logo Facebook). */
function GroupIcon({ group }: { group: Pick<ChannelGroup, 'platforms'> }) {
  return (
    <span className="relative inline-flex h-4 w-4 shrink-0">
      {group.platforms[0] != null ? <PlatformBadge platform={group.platforms[0]} /> : <Users className="h-4 w-4 text-muted-foreground" />}
    </span>
  )
}

export function AccountRail({
  accounts,
  accountId,
  onAccountChange,
  unreadByAccount,
  channelGroups,
  channel,
  onChannelChange,
  userId,
}: {
  accounts: ChannelAccount[]
  accountId?: string
  onAccountChange: (id?: string) => void
  unreadByAccount?: Record<string, number>
  channelGroups: ChannelGroup[]
  channel: ChannelFilterValue
  onChannelChange: (v: ChannelFilterValue) => void
  userId?: string | null
}) {
  const { t } = useTranslation('convList')
  const [expandOpen, setExpandOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [channelMenuOpen, setChannelMenuOpen] = useState(false)
  const [sortOpen, setSortOpen] = useState(false)

  // Kênh đã chọn ở ô chọn kênh (ngoài) lọc trước — hàng ảnh (trong) chỉ còn tài khoản thuộc kênh đó.
  const byChannel = useMemo(
    () => (channel === 'all' ? accounts : accounts.filter((a) => groupOfPlatform(a.platform) === channel)),
    [accounts, channel],
  )

  const { order, setOrder } = useMobileChannels(userId ?? undefined)
  const ordered = useMemo(() => orderAccounts(byChannel, order), [byChannel, order])
  const shown = useMemo(() => visibleAccounts(ordered, accountId, ROW_CHANNELS), [ordered, accountId])
  const unreadOf = (id: string) => otherAccountUnread(unreadByAccount, accountId, id)
  const hiddenUnread = useMemo(
    () => ordered.filter((a) => !shown.some((s) => s.id === a.id)).reduce((n, a) => n + unreadOf(a.id), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ordered, shown, unreadByAccount, accountId],
  )

  const matched = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return byChannel
    return byChannel.filter((a) => (a.displayName ?? '').toLowerCase().includes(q) || (a.phone ?? '').includes(q))
  }, [byChannel, query])

  const selectedGroup = channel !== 'all' ? channelGroups.find((g) => g.id === channel) : undefined

  return (
    <div className="space-y-1.5">
      {channelGroups.length > 1 && (
        <DropdownMenu open={channelMenuOpen} onOpenChange={setChannelMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('acct.pickChannel')}
              className="flex h-7 w-full items-center gap-1.5 rounded-md px-1.5 text-xs font-medium text-foreground hover:bg-card"
            >
              {selectedGroup ? <GroupIcon group={selectedGroup} /> : <Users className="h-4 w-4 shrink-0 text-muted-foreground" />}
              <span className="min-w-0 flex-1 truncate text-left">{selectedGroup?.label ?? t('acct.allChannels')}</span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuItem onSelect={() => onChannelChange('all')} className={cn('gap-2', channel === 'all' && 'bg-accent text-accent-foreground')}>
              <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{t('acct.allChannels')}</span>
              {channel === 'all' && <Check className="h-4 w-4 shrink-0" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {channelGroups.map((g) => (
              <DropdownMenuItem key={g.id} onSelect={() => onChannelChange(g.id)} className={cn('gap-2', channel === g.id && 'bg-accent text-accent-foreground')}>
                <GroupIcon group={g} />
                <span className="min-w-0 flex-1 truncate">{g.label}</span>
                {channel === g.id && <Check className="h-4 w-4 shrink-0" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {byChannel.length === 0 ? (
        <p className="px-1 py-1 text-xs text-muted-foreground">{t('acct.noneInGroup')}</p>
      ) : (
        <ul className="grid grid-cols-6 gap-x-0.5 gap-y-1" aria-label={t('chSheet.title')}>
          {/* Ô đầu LUÔN là "Tất cả" (không phụ thuộc kênh/tài khoản đang xếp) — bấm để bỏ lọc theo một tài khoản cụ thể. */}
          <li className="min-w-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-pressed={!accountId}
                  aria-label={t('chSheet.allAccounts')}
                  onClick={() => onAccountChange(undefined)}
                  className="flex w-full flex-col items-center gap-1"
                >
                  <span className={cn('relative flex h-8 w-8 items-center justify-center rounded-full bg-card', !accountId && 'ring-2 ring-primary ring-offset-2 ring-offset-subnav')}>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </span>
                  <span className={cn('w-full truncate text-center text-[9px] leading-tight', !accountId ? 'font-medium text-foreground' : 'text-muted-foreground')}>{t('common:misc.all')}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t('chSheet.allAccounts')}</TooltipContent>
            </Tooltip>
          </li>

          {shown.map((a) => {
            const name = a.displayName ?? t('shared.unnamed')
            const active = accountId === a.id
            const n = unreadOf(a.id)
            return (
              <li key={a.id} className="min-w-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-pressed={active}
                      aria-label={name}
                      onClick={() => onAccountChange(active ? undefined : a.id)}
                      className="flex w-full flex-col items-center gap-1"
                    >
                      <span className={cn('relative flex h-8 w-8 items-center justify-center rounded-full', active && 'ring-2 ring-primary ring-offset-2 ring-offset-subnav')}>
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={a.avatarUrl ?? undefined} alt={name} />
                          <AvatarFallback className="text-xs">{initials(a.displayName)}</AvatarFallback>
                        </Avatar>
                        <PlatformBadge platform={a.platform} />
                        {n > 0 && (
                          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold leading-none text-destructive-foreground ring-2 ring-subnav">
                            {shortUnreadCount(n)}
                          </span>
                        )}
                      </span>
                      <span className={cn('w-full truncate text-center text-[9px] leading-tight', active ? 'font-medium text-foreground' : 'text-muted-foreground')}>{name}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{name}</TooltipContent>
                </Tooltip>
              </li>
            )
          })}

          {hasExpand(byChannel.length) && (
            <li className="min-w-0">
              <DropdownMenu open={expandOpen} onOpenChange={setExpandOpen}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label={hiddenUnread > 0 ? t('acct.expandAriaUnread', { count: hiddenUnread }) : t('acct.expandAria')}
                        className="flex w-full flex-col items-center gap-1"
                      >
                        <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-card">
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          {hiddenUnread > 0 && (
                            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold leading-none text-destructive-foreground ring-2 ring-subnav">
                              {shortUnreadCount(hiddenUnread)}
                            </span>
                          )}
                        </span>
                        <span className="w-full truncate text-center text-[9px] leading-tight text-muted-foreground">{t('mhdr.expand')}</span>
                      </button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{t('acct.viewAll')}</TooltipContent>
                </Tooltip>

                <DropdownMenuContent
                  align="start"
                  side="right"
                  collisionPadding={8}
                  className="flex max-h-[var(--radix-dropdown-menu-content-available-height)] w-72 flex-col"
                >
                  <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                    <DropdownMenuLabel className="p-0">
                      {t('chSheet.title')}{channel !== 'all' ? ` · ${channelGroups.find((g) => g.id === channel)?.label}` : ''}
                    </DropdownMenuLabel>
                    {/* Sắp xếp thứ tự chỉ khi xem "Tất cả" — sắp trong danh sách đã lọc theo kênh sẽ lưu một thứ tự
                        chỉ gồm tài khoản đang thấy, âm thầm "quên" vị trí của tài khoản kênh khác (xem account-sort-dialog). */}
                    {channel === 'all' && hasExpand(byChannel.length) && (
                      <button
                        type="button"
                        onClick={() => { setExpandOpen(false); setSortOpen(true) }}
                        className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-primary hover:bg-accent"
                      >
                        <ArrowUpDown className="h-3 w-3" /> {t('common:actions.sort')}
                      </button>
                    )}
                  </div>
                  <DropdownMenuSeparator />

                  {byChannel.length > 10 && (
                    <div className="px-2 pb-1.5 pt-1">
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          placeholder={t('rail.searchAccounts')}
                          className="h-8 pl-7 text-xs"
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  )}

                  <DropdownMenuItem
                    onSelect={() => { onAccountChange(undefined); setExpandOpen(false) }}
                    className={cn('gap-2', !accountId && 'bg-accent text-accent-foreground')}
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border">
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </span>
                    <span className="truncate">{t('chSheet.allAccounts')}</span>
                  </DropdownMenuItem>

                  {matched.length > 0 && <DropdownMenuSeparator />}

                  <div className="max-h-[440px] min-h-0 overflow-y-auto">
                    {matched.map((acc) => {
                      const meta = statusMeta(acc.liveStatus, acc.isDisabled)
                      const name = acc.displayName ?? t('shared.unnamed')
                      const unreadHere = unreadOf(acc.id)
                      return (
                        <DropdownMenuItem
                          key={acc.id}
                          onSelect={() => { onAccountChange(acc.id); setExpandOpen(false) }}
                          className={cn('gap-2', accountId === acc.id && 'bg-accent text-accent-foreground')}
                        >
                          <span className="relative shrink-0">
                            <Avatar className="h-7 w-7">
                              <AvatarImage src={acc.avatarUrl ?? undefined} alt={name} />
                              <AvatarFallback className="text-xs">{initials(acc.displayName)}</AvatarFallback>
                            </Avatar>
                            <PlatformBadge platform={acc.platform} />
                          </span>
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="truncate">{name}</span>
                            <PlatformTag platform={acc.platform} className="self-start" />
                          </span>
                          {unreadHere > 0 && (
                            <span title={t('shared.unreadCount', { count: unreadHere })} className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
                              {shortUnreadCount(unreadHere)}
                            </span>
                          )}
                          {isAccountDown(acc) ? (
                            <span className="flex shrink-0 flex-col items-end gap-1">
                              <Badge variant={meta.variant} className="px-1.5 py-0 text-[10px]">{meta.label}</Badge>
                              <ReconnectButton account={acc} />
                            </span>
                          ) : (
                            <Badge variant={meta.variant} className="shrink-0 px-1.5 py-0 text-[10px]">{meta.label}</Badge>
                          )}
                        </DropdownMenuItem>
                      )
                    })}
                  </div>

                  {byChannel.length > 0 && matched.length === 0 && (
                    <div className="px-2 py-3 text-center text-xs text-muted-foreground">{t('rail.noMatchAccounts')}</div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          )}
        </ul>
      )}

      <AccountSortDialog open={sortOpen} onOpenChange={setSortOpen} accounts={ordered} order={order} onReorder={setOrder} />
    </div>
  )
}

/** Hộp "Sắp xếp tài khoản kênh" — kéo-thả (chuột) hoặc mũi tên lên/xuống, giống hệt hộp trên điện thoại
 * (`mobile-channels-sheet.tsx`). Tách khỏi `DropdownMenu` ở trên vì kéo-thả bên trong một menu Radix đang
 * mở dễ vướng cơ chế giữ con trỏ/focus-trap của chính menu đó — hộp riêng (Dialog) không có xung đột này. */
function AccountSortDialog({ open, onOpenChange, accounts, order, onReorder }: {
  open: boolean
  onOpenChange: (v: boolean) => void
  /** Đã sắp theo thứ tự hiện tại (orderAccounts áp dụng sẵn). */
  accounts: ChannelAccount[]
  order: readonly string[]
  onReorder: (ids: string[]) => void
}) {
  const { t } = useTranslation('convList')
  const ids = useMemo(() => accounts.map((a) => a.id), [accounts])
  const drag = useDragReorder(ids, onReorder)
  const isDefault = order.length === 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('acct.sortTitle')}</DialogTitle>
          <DialogDescription>
            {t('acct.sortDesc')}
          </DialogDescription>
        </DialogHeader>
        <ul ref={drag.listRef} className="relative max-h-[60vh] divide-y overflow-y-auto border-y" aria-label={t('chSheet.sortedAria')}>
          {accounts.map((a, i) => {
            const name = a.displayName || t('shared.unnamed')
            const dragging = drag.active?.id === a.id
            return (
              <li
                key={a.id}
                ref={drag.rowRef(a.id)}
                className={cn('flex items-center gap-1 pr-1', dragging && 'relative z-10 bg-background shadow-lg ring-1 ring-primary/30')}
                style={dragging ? { transform: `translateY(${drag.active!.dy}px)` } : undefined}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label={t('segSheet.dragAria', { label: name })}
                  {...drag.grip(a.id)}
                  style={{ touchAction: 'none' }}
                  className={cn('flex h-11 w-8 shrink-0 cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing', dragging && 'text-primary')}
                >
                  <GripVertical className="h-4 w-4" />
                </button>
                <span className="relative flex h-8 w-8 shrink-0 items-center justify-center">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={a.avatarUrl ?? undefined} alt={name} />
                    <AvatarFallback className="text-xs">{initials(a.displayName)}</AvatarFallback>
                  </Avatar>
                  <PlatformBadge platform={a.platform} />
                </span>
                <span className="min-w-0 flex-1 truncate py-2.5 text-sm">{name}</span>
                <button type="button" aria-label={t('segSheet.moveUp', { label: name })} disabled={i === 0} onClick={() => onReorder(moveAccountId(ids, a.id, -1))} className="flex h-8 w-8 items-center justify-center rounded-md text-foreground hover:bg-muted disabled:opacity-25">
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button type="button" aria-label={t('segSheet.moveDown', { label: name })} disabled={i === accounts.length - 1} onClick={() => onReorder(moveAccountId(ids, a.id, 1))} className="flex h-8 w-8 items-center justify-center rounded-md text-foreground hover:bg-muted disabled:opacity-25">
                  <ArrowDown className="h-4 w-4" />
                </button>
              </li>
            )
          })}
          {drag.active && drag.active.to !== drag.active.from && (
            <li aria-hidden className="pointer-events-none absolute inset-x-0 z-20 h-0.5 -translate-y-px rounded-full bg-primary" style={{ top: drag.active.lineY }} />
          )}
        </ul>
        <DialogFooter className="sm:justify-between">
          <Button variant="outline" size="sm" disabled={isDefault} onClick={() => onReorder([])}>
            <RotateCcw className="h-3.5 w-3.5" /> {t('shared.default')}
          </Button>
          <Button size="sm" onClick={() => onOpenChange(false)}>{t('common:actions.done')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
